import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { LOGO_URL } from "@/data/content";

const Section = ({ n, title, children }) => (
  <section className="border-t border-white/10 py-12" data-testid={`privacy-section-${n}`}>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-3">
        <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">
          {n}
        </span>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold uppercase leading-tight tracking-tight text-white">
          {title}
        </h2>
      </div>
      <div className="md:col-span-9 space-y-5 text-sm sm:text-base font-light leading-relaxed text-zinc-400">
        {children}
      </div>
    </div>
  </section>
);

const Ext = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-zinc-200 underline decoration-white/30 underline-offset-4 hover:text-white hover:decoration-white transition-colors"
  >
    {children}
    <ArrowUpRight className="h-3.5 w-3.5" />
  </a>
);

export default function Privacy() {
  return (
    <div className="relative min-h-screen bg-black text-zinc-200">
      <div className="pointer-events-none absolute inset-0 spotlight-top opacity-40" />
      <div className="relative mx-auto max-w-5xl px-5 sm:px-8 lg:px-12 py-14 sm:py-20">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between mb-14">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-logo">
            <img src={LOGO_URL} alt="Vision Studio" className="h-10 w-auto object-contain" />
            <span className="font-display text-sm font-semibold tracking-[0.3em] text-white uppercase">
              Vision Studio
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-300 hover:border-white hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Site
          </Link>
        </div>

        <div className="mb-14">
          <span className="mb-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">
            <span className="h-px w-10 bg-zinc-600" />
            Privacy Policy
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] tracking-tight text-chrome">
            How we handle your information
          </h1>
          <p className="mt-6 max-w-2xl text-sm text-zinc-500">
            Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>

        <Section n="01" title="Overview">
          <p>
            This Privacy Policy explains what information Vision Studio (&ldquo;we&rdquo;,
            &ldquo;us&rdquo;) collects when you use{" "}
            <span className="text-zinc-200">thevisionstudio.in</span>, why we
            collect it, and the choices you have. We collect only the minimum
            required to reply to your enquiries and to keep the site secure.
          </p>
        </Section>

        <Section n="02" title="Information we collect">
          <p>
            When you use the enquiry form we collect the details you provide —
            your name, email address, company (optional), the service you are
            interested in (optional) and your message. We do not use tracking
            cookies for advertising, and we do not sell or share your data with
            third parties for marketing.
          </p>
          <p>
            For anti-abuse purposes, our forms are protected by Cloudflare
            Turnstile which may process limited technical signals from your
            browser to distinguish humans from bots — see Section 05 below.
          </p>
        </Section>

        <Section n="03" title="How we use it">
          <p>
            Your enquiry is used solely to respond to you about the project you
            described, to prepare quotes and to schedule any follow-up
            conversations. We do not add you to any newsletter or marketing
            list.
          </p>
        </Section>

        <Section n="04" title="Storage & retention">
          <p>
            Enquiries are stored in a private database and are accessible only
            to authenticated members of the Vision Studio team via a protected
            admin console. Notification emails are delivered to our inbox at{" "}
            <a
              href="mailto:hello@thevisionstudio.in"
              className="text-zinc-200 hover:text-white transition-colors"
            >
              hello@thevisionstudio.in
            </a>{" "}
            through a transactional email provider. Records are retained for as
            long as necessary to complete the conversation and any resulting
            engagement, after which they may be deleted on request.
          </p>
        </Section>

        <Section n="05" title="Cloudflare Turnstile (bot protection)">
          <p>
            This site uses Cloudflare Turnstile to protect the enquiry form
            from automated spam. Turnstile runs a lightweight, privacy-first
            challenge that in most cases is invisible to the visitor. To make
            that determination, Cloudflare processes information such as your
            IP address, browser characteristics and interaction signals for the
            sole purpose of distinguishing humans from bots. Turnstile does not
            use tracking cookies and does not fingerprint users for advertising
            purposes.
          </p>
          <p>
            Your use of the enquiry form is subject to the Cloudflare Privacy
            Policy and Terms of Service. For details, see the{" "}
            <Ext href="https://www.cloudflare.com/application-services/terms/turnstile-privacy-addendum/">
              Cloudflare Turnstile Privacy Addendum
            </Ext>
            , the{" "}
            <Ext href="https://www.cloudflare.com/privacypolicy/">
              Cloudflare Privacy Policy
            </Ext>{" "}
            and the{" "}
            <Ext href="https://www.cloudflare.com/website-terms/">
              Cloudflare Website Terms of Service
            </Ext>
            .
          </p>
        </Section>

        <Section n="06" title="Your rights">
          <p>
            You can ask us at any time to access, correct or delete the
            information you have shared with us. Write to{" "}
            <a
              href="mailto:hello@thevisionstudio.in"
              className="text-zinc-200 hover:text-white transition-colors"
            >
              hello@thevisionstudio.in
            </a>{" "}
            and we will respond within a reasonable time.
          </p>
        </Section>

        <Section n="07" title="Contact">
          <p>
            Vision Studio · 408, Mohid Heights, Suresh Nagar, RTO Lane,
            Andheri West, Mumbai — 400053. Email{" "}
            <a
              href="mailto:hello@thevisionstudio.in"
              className="text-zinc-200 hover:text-white transition-colors"
            >
              hello@thevisionstudio.in
            </a>{" "}
            for any privacy-related questions.
          </p>
        </Section>

        <div className="mt-14 flex justify-end">
          <Link
            to="/"
            data-testid="privacy-back-bottom"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Site
          </Link>
        </div>
      </div>
    </div>
  );
}
